import { format } from 'date-fns';
import { AnimatePresence, motion } from "framer-motion";
import { FiBell, FiClock, FiTrendingUp, FiUsers } from "react-icons/fi";
import { Link } from 'react-router-dom';
import { useAnnouncements } from "../../contexts/AnnouncementsContext";

const RecentAnnouncements = () => {
  const { announcements } = useAnnouncements();
  const recentAnnouncements = announcements?.slice(0, 5) || [];

  if (!recentAnnouncements.length) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center py-12"
      >
        <motion.div
          animate={{ 
            rotate: [0, 10, -10, 0],
            scale: [1, 1.1, 1]
          }}
          transition={{ 
            duration: 2,
            repeat: Infinity,
            repeatDelay: 3
          }}
          className="mx-auto h-16 w-16 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center mb-4 shadow-lg"
        >
          <FiBell className="h-8 w-8 text-white" />
        </motion.div>
        <p className="text-gray-500 font-medium">
          No recent announcements
        </p>
        <p className="text-sm text-gray-400 mt-1">
          Create your first announcement to get started
        </p>
      </motion.div>
    );
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100
      }
    }
  };

  const getTargetColor = (targetType) => {
    switch (targetType) {
      case 'members':
        return 'from-blue-500 to-cyan-500';
      case 'leaders':
        return 'from-purple-500 to-pink-500';
      case 'youth':
        return 'from-green-500 to-emerald-500';
      default:
        return 'from-gray-500 to-gray-600';
    }
  };

  const getTargetIcon = (targetType) => {
    switch (targetType) {
      case 'members':
        return <FiUsers className="h-3 w-3" />;
      case 'leaders':
        return <FiTrendingUp className="h-3 w-3" />;
      case 'youth':
        return <FiUsers className="h-3 w-3" />;
      default:
        return <FiUsers className="h-3 w-3" />;
    }
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-4"
    >
      <Link to="/admin/announcements">
      <AnimatePresence>
        {recentAnnouncements.map((announcement, index) => (
          <motion.div
            key={announcement.id}
            variants={itemVariants}
            layout
            whileHover={{ 
              scale: 1.02,
              transition: { type: "spring", stiffness: 400, damping: 25 }
            }}
            className="group relative overflow-hidden rounded-xl backdrop-blur-sm bg-white/60 border border-white/20 p-5 hover:shadow-lg transition-all duration-300"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-purple-500/5 to-pink-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            
            <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-purple-500 to-pink-500 opacity-60" />
            
            <div className="relative z-10">
              <div className="flex justify-between items-start mb-3">
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-gray-900 group-hover:text-purple-600 transition-colors duration-200 truncate">
                    {announcement.title}
                  </h4>
                  <div className="flex items-center mt-1 text-xs text-gray-500">
                    <FiClock className="h-3 w-3 mr-1" />
                    {format(new Date(announcement.publishDate), 'MMM d, yyyy')}
                  </div>
                </div>
                
                <motion.div
                  whileHover={{ rotate: 15 }}
                  className="ml-3 p-2 rounded-lg bg-gradient-to-br from-purple-100 to-pink-100"
                >
                  <FiBell className="h-4 w-4 text-purple-600" />
                </motion.div>
              </div>
              
              <p className="text-sm text-gray-600 line-clamp-2 mb-3 leading-relaxed">
                {announcement.description}
              </p>
              
              <div className="flex items-center justify-between">
                {announcement.targetType && announcement.targetType !== 'all' && (
                  <motion.span
                    whileHover={{ scale: 1.05 }}
                    className={`inline-flex items-center px-3 py-1 text-xs font-medium rounded-full bg-gradient-to-r ${getTargetColor(announcement.targetType)} text-white shadow-sm`}
                  >
                    {getTargetIcon(announcement.targetType)}
                    <span className="ml-1 capitalize">{announcement.targetType}</span>
                  </motion.span>
                )}
                
                <div className="flex items-center space-x-2 ml-auto">
                  <div className="flex -space-x-1">
                    {[...Array(3)].map((_, i) => (
                      <div
                        key={i}
                        className={`w-6 h-6 rounded-full border-2 border-white ${
                          i === 0 ? 'from-blue-400 to-blue-600' :
                          i === 1 ? 'from-green-400 to-green-600' :
                          'from-purple-400 to-purple-600'
                        }`}
                      />
                    ))}
                  </div>
                  <span className="text-xs text-gray-500 font-medium">
                    +{12 + index * 3}
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </AnimatePresence>
      </Link>
    </motion.div>
  );
};

export default RecentAnnouncements;